import { User } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL;

/** A catalog recipe as the trending admin endpoint returns it for display. */
export type TrendingCatalogRecipe = {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  cuisine?: string;
  difficulty?: string;
  totalTimeMinutes?: number;
  servings?: number;
  isCurated: boolean;
  dietaryRestrictions: string[];
  tags: string[];
};

export type TrendingEntry = {
  rank: number;
  catalogRecipeId: string;
  name: string;
  /** Distinct households that cooked it in the window. The ranking's honesty check. */
  cookCount: number;
  /** Recency-weighted cookCount. The ordering key. */
  score: number;
  lastCookedAt: string | null;
  /** Null when the recipe was deleted from the catalog after the scan ran. */
  recipe: TrendingCatalogRecipe | null;
};

export type TrendingSnapshot = {
  /** Null means the scan has never run, which is not the same as an empty list. */
  computedAt: string | null;
  scannedEvents?: number;
  windowDays?: number | null;
  minDistinctCooks?: number | null;
  recipes: TrendingEntry[];
};

export type TrendingRefreshResult = {
  scannedEvents: number;
  resolvedToCatalog: number;
  distinctCatalogRecipes: number;
  published: number;
  windowDays: number;
  minDistinctCooks: number;
};

const getHeaders = async (user: User) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${await user.getIdToken()}`,
});

export const fetchTrendingRecipes = async (
  user: User
): Promise<TrendingSnapshot> => {
  const response = await fetch(`${API_URL}/api/admin/trending-recipes`, {
    headers: await getHeaders(user),
  });

  if (!response.ok) throw new Error('Failed to load trending recipes');
  return response.json();
};

export const refreshTrendingRecipes = async (
  user: User
): Promise<TrendingRefreshResult> => {
  const response = await fetch(
    `${API_URL}/api/admin/trending-recipes/refresh`,
    { method: 'POST', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to refresh trending recipes');
  }

  return response.json();
};
